// @ts-nocheck
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import PreGameOverlay from 'components/playback/PreGameOverlay';
import ClosedCaptionsOverlay from 'components/common/ClosedCaptionsOverlay';
import FocusGroup from 'components/focus/FocusGroup';
import { Focusable } from 'components/focus';
import applicationActions from 'common/support/applicationActions';
import { isLiveVideo, isPreGame } from 'support/videoHelper';
import {
  usePlayerOverlay,
  useDiscoveryRowOverlay,
  useFFRW,
} from 'hooks/playback';
import { useFocusManager } from 'hooks/focus';
import { useCurrentVideo, selectRows } from 'hooks/video';
import textToSpeechManager from 'support/tts/TextToSpeechManager';
import useTimeout, { useImperativeTimer } from 'hooks/timeout';
import useTracking from 'hooks/analytics';
import { getPageValues } from 'support/analytics/analyticsHelper';

import {
  usePlayerState,
  usePlayerAction,
} from 'components/providers/PlayerStateContext';
import useThrottledCallback from 'hooks/throttledCallback';
import { getDurationTTS } from 'support/textToSpeechHelper';

import DiscoveryRowOverlay from 'components/pages/playback/DiscoveryRowOverlay';

import { useAreYouStillWatchingTimer } from 'components/pages/AreYouStillWatching';
import { exitFullScreenPlayer, showFullScreenPlayer } from 'actions/hero';
import PlayerNotification from './PlayerNotification';
import styles from './PlayerOverlay.module.scss';
import PlaybackButton from './PlaybackButton';
import PlaybackTimeline from './PlaybackTimeline';

const messages = {
  overlayBugLive: {
    id: 'playerOverlay.bug.live',
    description: 'Live Bug Text',
  },
  adCountdown: {
    id: 'playerOverlay.ad.countdown',
    description: 'Ad Countdown',
  },
  playbackRateText: {
    id: 'playerOverlay.scrub.speed',
    description: 'Scrubbing speed',
  },
  ttsLive: {
    id: 'playerOverlay.tts.live',
    description: 'TTS for Live Bug',
  },

  ttsRewindButton: {
    id: 'playerOverlay.button.rewind',
    description: 'Text for rewind button',
  },
  ttsPlayButton: {
    id: 'playerOverlay.button.play',
    description: 'Text for play button',
  },
  ttsPauseButton: {
    id: 'playerOverlay.button.pause',
    description: 'Text for pause button',
  },
  ttsFastForwardButton: {
    id: 'playerOverlay.button.fastForward',
    description: 'Text for fast forward button',
  },

  ttsRewindAction: {
    id: 'playerOverlay.tts.rewind',
    description: 'TTS for rewind action',
  },
  ttsPlayAction: {
    id: 'playerOverlay.tts.play',
    description: 'TTS for play action',
  },
  ttsPauseAction: {
    id: 'playerOverlay.tts.paused',
    description: 'TTS for pause action',
  },
  ttsFastForwardAction: {
    id: 'playerOverlay.tts.fastForward',
    description: 'TTS for fast forward action',
  },
};

const FocusId = {
  PLAY_PAUSE: 'play-pause-button',
  FAST_FORWARD: 'ff-button',
  REWIND: 'rw-button',
  FOCUS_HOLDER: 'focus-holder',
  DISCOVERY_ROW: 'discovery-row',
};

const PlayerOverlay = ({ onPrevVideo, onNextVideo, fullScreenPlayer }) => {
  const intl = useIntl();
  const history = useHistory();
  const { trackEvent } = useTracking();
  const focusManager = useFocusManager();
  const dispatch = useDispatch();
  const {
    isPlayerOverlayVisible,
    igniteOverlay,
    enableAutoHide,
    hideOverlay,
  } = usePlayerOverlay();
  const {
    isOverlayDiscoveryVisible,
    igniteDiscoveryOverlay,
    hideDiscoveryOverlay,
    enableAutoHideDiscovery,
  } = useDiscoveryRowOverlay();
  useAreYouStillWatchingTimer();
  const {
    isContentStarted,
    isPlaying,
    position,
    isAdActive,
    adPosition,
    adDuration,
    duration: streamDuration,
  } = usePlayerState();
  const { play, pause, seek } = usePlayerAction();

  const [video, row] = useCurrentVideo();
  const videoDuration = video?.about?.duration || 0;

  const isLive = isLiveVideo(video);
  const isStreamActive = isContentStarted || isAdActive;
  const isLimitedControls = isLive || isAdActive;

  const heroData = useSelector(state => state.kitchenSink.data?.hero?.data);

  const ksRowData = useSelector(state => state.kitchenSink.data.rows);

  const page = useSelector(state => state.pageView?.page);
  const type = useSelector(state => state.pageView?.type);

  const pageRowData = useSelector(state => {
    const rows = selectRows(state, page, type);
    return rows;
  });

  const rotatingSegmentTitles = useSelector(
    state => state.rotatingSegmentTitles,
  );

  const segmentTitlesEnabled = useSelector(
    state => state.kitchenSink.data?.config?.modules?.SegmentTitles?.enabled,
  );

  let title = video?.about?.shortTitle;

  if (segmentTitlesEnabled && rotatingSegmentTitles?.data?.titleData) {
    if (
      Object.keys(rotatingSegmentTitles.data.titleData).find(
        key => key === video?.id,
      ) !== undefined
    ) {
      title =
        rotatingSegmentTitles?.data?.titleData[video?.id].segmentTitle !== null
          ? rotatingSegmentTitles?.data?.titleData[video?.id].segmentTitle
          : title;
    }
  }

  useEffect(() => {
    if (!isStreamActive) {
      hideOverlay();
      hideDiscoveryOverlay();
    }
  }, [hideOverlay, hideDiscoveryOverlay, isStreamActive]);

  const timerRef = useRef();

  timerRef.current = useImperativeTimer(() => {
    enableAutoHideDiscovery(true);
  }, 300);

  useEffect(() => {
    if (!isOverlayDiscoveryVisible && !isPlayerOverlayVisible) {
      focusManager.setCurrentFocus(FocusId.FOCUS_HOLDER);
    }
  }, [isOverlayDiscoveryVisible, isPlayerOverlayVisible, focusManager]);

  useEffect(() => {
    const onFocusChangedListener = () => {
      if (isOverlayDiscoveryVisible) {
        enableAutoHideDiscovery(false);
        timerRef.current.restart();
      }
    };
    focusManager.addFocusChangedListener(onFocusChangedListener);
    return () => {
      focusManager.removeFocusChangedListener(onFocusChangedListener);
    };
  }, [focusManager, isOverlayDiscoveryVisible, enableAutoHideDiscovery]);

  const [bannerMode, setBannerMode] = useState(true);
  useTimeout(() => {
    if (bannerMode) setBannerMode(false);
  }, 5000);

  const showAYSW = useSelector(state => state.areYouStillWatching.showDialog);

  useEffect(() => {
    if (isPlayerOverlayVisible && isStreamActive) {
      if (isLive) {
        textToSpeechManager.add([intl.formatMessage(messages.ttsLive), title]);
        textToSpeechManager.speak(); // No progress bar and no playback button for Live Event
        focusManager.setCurrentFocus(FocusId.FOCUS_HOLDER);
      } else {
        textToSpeechManager.add(title);
        focusManager.setCurrentFocus(FocusId.PLAY_PAUSE); // Set focus and speak
      }
    } else if (!showAYSW) {
      focusManager.setCurrentFocus(FocusId.FOCUS_HOLDER);
    }
  }, [
    focusManager,
    intl,
    isLive,
    isPlayerOverlayVisible,
    isStreamActive,
    title,
    showAYSW,
  ]);

  const onPlaybackRateChange = useCallback(
    rate => {
      if (rate !== 1) pause();
    },
    [pause],
  );
  const handleSeek = useCallback(
    seekPosition => {
      seek(Math.floor(seekPosition));
      play();
    },
    [play, seek],
  );

  const {
    label: playbackRateText,
    playbackRate,
    seekTime,
    ff: dispatchFf,
    rw: dispatchRw,
    seek: dispatchSeek,
  } = useFFRW(
    position,
    streamDuration || videoDuration,
    handleSeek,
    onPlaybackRateChange,
  );

  const isSeeking = playbackRate !== 1;

  // Note: when seeking, position from usePlayerState may lag so we need to
  // pass progress/duration to NotificationOverlay explicitly.
  const progress = seekTime !== null ? seekTime : position;
  const duration = streamDuration || videoDuration;

  const adCountdown = adDuration - adPosition;

  const isCcEnabled = useSelector(state => state.closedCaptioning);

  const positionTts = useRef();
  useEffect(() => {
    positionTts.current = getDurationTTS(progress, intl, true);
  }, [intl, progress]);

  useEffect(() => {
    if (isSeeking) {
      enableAutoHide(false);
    } else {
      enableAutoHide(true);
    }
  }, [enableAutoHide, isSeeking]);

  const trySeekingBeforeAction = useCallback(
    action => {
      if (isLive) return;
      if (isSeeking) {
        dispatchSeek();
        return;
      }
      action();
    },
    [dispatchSeek, isLive, isSeeking],
  );

  const updateFocus = focusId => {
    if (focusManager.currentFocusId !== focusId) {
      focusManager.setCurrentFocus(focusId);
    }
  };

  const speakAndAction = useCallback(
    (message, action, messageValues = {}) => {
      textToSpeechManager.add(intl.formatMessage(message, messageValues));
      textToSpeechManager.speak();
      action();
    },
    [intl],
  );

  const onPlayPause = () => {
    if (!isStreamActive) return;

    updateFocus(FocusId.PLAY_PAUSE);
    trySeekingBeforeAction(() => {
      if (isPlaying)
        speakAndAction(messages.ttsPauseAction, pause, {
          time: positionTts.current,
        });
      else
        speakAndAction(messages.ttsPlayAction, play, {
          time: positionTts.current,
        });
    });
  };

  const onPlay = () => {
    if (!isStreamActive) return;

    updateFocus(FocusId.PLAY_PAUSE);
    trySeekingBeforeAction(() => {
      if (!isPlaying)
        speakAndAction(messages.ttsPlayAction, play, {
          time: positionTts.current,
        });
    });
  };

  const onPause = () => {
    if (!isStreamActive) return;

    updateFocus(FocusId.PLAY_PAUSE);
    trySeekingBeforeAction(() => {
      if (isPlaying)
        speakAndAction(messages.ttsPauseAction, pause, {
          time: positionTts.current,
        });
    });
  };

  const onRewind = () => {
    if (isLimitedControls || !isStreamActive) return;
    updateFocus(FocusId.REWIND);
    dispatchRw();
  };

  const onFastForward = () => {
    if (isLimitedControls || !isStreamActive) return;
    updateFocus(FocusId.FAST_FORWARD);
    dispatchFf();
  };

  const shouldShowOverlay = action => {
    if (
      isStreamActive &&
      (applicationActions.isCoreAction(action) ||
        applicationActions.isSeekAction(action) ||
        applicationActions.isPlayPauseAction(action) ||
        action === applicationActions.stop) &&
      !applicationActions.isUpDownAction(action) &&
      !isOverlayDiscoveryVisible
    )
      return true;

    return false;
  };

  const shouldShowDiscoveryOverlay = action => {
    if (isStreamActive && action === applicationActions.moveDown) return true;

    return false;
  };

  const shouldHideOverlay = action => {
    if (isStreamActive && action === applicationActions.moveUp) return true;

    return false;
  };

  const hideDiscoveryOverlayonClick = () => {
    hideDiscoveryOverlay();
    updateFocus(FocusId.FOCUS_HOLDER);
  };

  const isBackAction = action => {
    if (action === applicationActions.back) return true;

    return false;
  };

  const onAction = action => {
    if (isBackAction(action)) {
      if (isPlayerOverlayVisible || isOverlayDiscoveryVisible) {
        if (isPlayerOverlayVisible) {
          hideOverlay();
          trackEvent('playback interaction', {
            ...getPageValues('main'),
            pageType: 'main',
            moduleName: 'peek view',
            moduleLocation: 'On Now',
            moduleAction: 'collapse',
            moduleCampaign: 'video discovery row',
          });
        }
        hideDiscoveryOverlay();
        updateFocus(FocusId.FOCUS_HOLDER);
        return true;
      }

      if (fullScreenPlayer) {
        history.goBack();
        dispatch(exitFullScreenPlayer());
        return true;
      }

      const searchParamsCurrent = new URLSearchParams(
        history.entries[history.index].search,
      );

      const isFromDiscoveryRow = searchParamsCurrent.get('isDiscoveryRowVideo');

      // go back one index
      history.goBack();

      const searchParams = new URLSearchParams(
        history.entries[history.index].search,
      );
      const videoId = searchParams.get('video');
      const rowSlug = searchParams.get('rowSlug');
      const isDiscoveryRowVideo = searchParams.get('isDiscoveryRowVideo');
      const videoRow = pageRowData?.find(rowData => rowData.slug === rowSlug);
      const discoveryRowVideoRow = ksRowData?.find(
        rowData => rowData.slug === rowSlug,
      );

      if (history.index === 0) {
        // index is already at home page
        return true;
      }

      // hero carousel logic
      if (
        history.entries.length - history.index <= 2 &&
        history.entries[history.index].pathname === '/hero' &&
        isFromDiscoveryRow
      ) {
        // started from hero carousel player and pressed onto a discovery row video
        // have not pressed back once yet
        // pressing back from the discovery row video has led to hero carousel player

        if (
          heroData.find(videoItem => videoItem.id === videoId) !== undefined
        ) {
          // play hero video if it still exists in the hero carousel
          dispatch(showFullScreenPlayer(videoId));
        } else {
          // if video no longer exists in hero carousel, go back to home
          history.goBack();
        }

        return true;
      }

      // interior page logic
      let interiorPageVisited = false;
      let interiorPageIndex;

      // check if interior page has been visited
      for (let { index } = history; index > 0; index -= 1) {
        // check for closest interior page below current index
        if (
          history.entries[index].pathname !== '/playback' &&
          history.entries[index].pathname !== '/hero'
        ) {
          // interior page visited
          // save interior page history index
          interiorPageVisited = true;
          interiorPageIndex = index;
          break;
        }
      }

      if (interiorPageVisited) {
        if (
          history.entries.length - history.index > 2 ||
          (isDiscoveryRowVideo &&
            discoveryRowVideoRow?.data.find(
              videoItem => videoItem.id === videoId,
            ) === undefined) ||
          (!isDiscoveryRowVideo &&
            videoRow?.data.find(videoItem => videoItem.id === videoId) ===
              undefined)
        ) {
          // already pressed back once to previous video
          // OR
          // discovery row video currently doesn't exist in the kitchen sink (has been unpublished)
          // OR
          // interior page video currently doesn't exist in the kitchen sink (has been unpublished)
          // go to most recently visited interior page in the saved history
          history.go(-(history.index - interiorPageIndex));
        }
        return true;
      }

      // home page logic
      if (
        history.entries.length - history.index > 2 ||
        videoRow?.data.find(videoItem => videoItem.id === videoId) === undefined
      ) {
        // already pressed back once to previous video
        // OR
        // video currently doesn't exist in the kitchen sink (has been unpublished)
        // go to home page
        history.go(-history.index);
      }

      return true;
    }
    if (shouldShowOverlay(action)) {
      igniteOverlay();
    }

    if (shouldShowDiscoveryOverlay(action)) {
      hideOverlay();
      igniteDiscoveryOverlay();
    }

    if (shouldHideOverlay(action)) {
      if (isPlayerOverlayVisible) {
        hideOverlay();
        trackEvent('playback interaction', {
          ...getPageValues('main'),
          pageType: 'main',
          moduleName: 'peek view',
          moduleLocation: 'On Now',
          moduleAction: 'collapse',
          moduleCampaign: 'video discovery row',
        });
      }
      hideDiscoveryOverlay();
    }

    switch (action) {
      case applicationActions.select:
        if (bannerMode) {
          setBannerMode(false);
          return true;
        }
        return false;
      case applicationActions.moveDown: {
        updateFocus(FocusId.DISCOVERY_ROW);
        return true;
      }
      case applicationActions.moveUp: {
        if (
          focusManager.currentFocusId !== FocusId.PLAY_PAUSE &&
          focusManager.currentFocusId !== FocusId.REWIND &&
          focusManager.currentFocusId !== FocusId.FAST_FORWARD
        ) {
          updateFocus(FocusId.FOCUS_HOLDER);
        }
        return true;
      }
      case applicationActions.playPause: {
        onPlayPause();
        return true;
      }
      case applicationActions.stop:
      case applicationActions.pause: {
        onPause();
        return true;
      }
      case applicationActions.play: {
        onPlay();
        return true;
      }
      case applicationActions.fastForward: {
        onFastForward();
        return true;
      }
      case applicationActions.rewind: {
        onRewind();
        return true;
      }
      case applicationActions.prevTrack: {
        if (!isLimitedControls && isStreamActive) onPrevVideo();
        return true;
      }
      case applicationActions.nextTrack: {
        if (!isLimitedControls && isStreamActive) onNextVideo();
        return true;
      }
      default:
        return false;
    }
  };

  const onMouseMove = useThrottledCallback(() => {
    if (isStreamActive && !isOverlayDiscoveryVisible) igniteOverlay();
  }, 500);

  const playPauseFocusedStyle = isPlaying
    ? styles.pauseFocused
    : styles.playFocused;

  const playPauseUnfocusedStyle = isPlaying
    ? styles.pauseUnfocused
    : styles.playUnfocused;

  useEffect(() => {
    const readProgressTTS = () => {
      textToSpeechManager.add(
        intl.formatMessage(
          messages[
            playbackRate < 0 ? 'ttsRewindAction' : 'ttsFastForwardAction'
          ],
          {
            speed: Math.abs(playbackRate),
            time: positionTts.current,
          },
        ),
      );
      textToSpeechManager.speak();
    };

    if (playbackRate !== 1) {
      readProgressTTS();
      const interval = setInterval(() => {
        readProgressTTS();
      }, 7000);
      return () => {
        clearInterval(interval);
      };
    }
    return undefined;
  }, [intl, playbackRate]);

  // TTS handling for player action change
  const prevPlaybackRate = useRef(playbackRate);

  useEffect(() => {
    if (!isSeeking && prevPlaybackRate.current !== 1) {
      textToSpeechManager.add(
        intl.formatMessage(messages.ttsPlayAction, {
          time: positionTts.current,
        }),
      );
      textToSpeechManager.speak();
    }
    prevPlaybackRate.current = playbackRate;
  }, [intl, isSeeking, playbackRate]);

  return (
    <React.Fragment>
      <Focusable
        focusId={FocusId.FOCUS_HOLDER}
        onAction={onAction}
        render={() => null}
      />
      <DiscoveryRowOverlay
        isOverlayDiscoveryVisible={isOverlayDiscoveryVisible}
        onAction={onAction}
        hideDiscoveryOverlayonClick={hideDiscoveryOverlayonClick}
      />
      <React.Fragment>
        <div
          className={classNames(
            'w-100',
            'd-flex',
            'flex-column',
            'justify-content-end',
            'align-items-center',
            'text-uppercase',
            'background-gradient',
            styles.controlsContainer,
            {
              [styles.hidden]: !isPlayerOverlayVisible,
              [styles.visible]: isPlayerOverlayVisible,
            },
          )}
          onMouseMove={onMouseMove}
        >
          {isLive && (
            <div className={classNames(styles.liveIcon)}>
              <FormattedMessage {...messages.overlayBugLive} />
            </div>
          )}
          <div className={classNames(styles.rowTitle)}> {row?.title} </div>
          <div
            className={classNames(
              'w-100 text-truncate text-center',
              styles.title,
            )}
          >
            {title}
          </div>
          {!isLimitedControls && (
            <FocusGroup
              className={classNames(
                'd-flex',
                'flex-row',
                'justify-content-between',
                styles.controls,
              )}
              onActionOverride={onAction}
              horizontal
            >
              {!isLimitedControls && (
                <div
                  className={classNames(
                    'd-flex',
                    'justify-content-end',
                    styles.seekButton,
                  )}
                >
                  <PlaybackButton
                    focusId={FocusId.REWIND}
                    onSelect={onRewind}
                    ttsText={intl.formatMessage(messages.ttsRewindButton)}
                    render={hasFocus => (
                      <div
                        className={classNames(
                          'd-flex',
                          'justify-content-center',
                          'align-items-center',
                          styles.rw,
                          hasFocus ? styles.rwFocused : styles.rwUnfocused,
                        )}
                      >
                        {playbackRate < -1 && (
                          <div className={classNames(styles.playbackRate)}>
                            <FormattedMessage
                              {...messages.playbackRateText}
                              values={{ speed: playbackRateText }}
                            />
                          </div>
                        )}
                        <img alt="Rewind" />
                      </div>
                    )}
                  />
                </div>
              )}
              {!isLive && (
                <PlaybackButton
                  className={styles.playPauseButton}
                  focusId={FocusId.PLAY_PAUSE}
                  onSelect={onPlayPause}
                  ttsText={intl.formatMessage(
                    isPlaying
                      ? messages.ttsPauseButton
                      : messages.ttsPlayButton,
                  )}
                  render={hasFocus => (
                    <img
                      alt={isPlaying ? 'Pause' : 'Play'}
                      className={
                        hasFocus
                          ? playPauseFocusedStyle
                          : playPauseUnfocusedStyle
                      }
                    />
                  )}
                />
              )}
              {!isLimitedControls && (
                <div className={classNames('d-flex', styles.seekButton)}>
                  <PlaybackButton
                    focusId={FocusId.FAST_FORWARD}
                    onSelect={onFastForward}
                    ttsText={intl.formatMessage(messages.ttsFastForwardButton)}
                    render={hasFocus => (
                      <div
                        className={classNames(
                          'd-flex',
                          'justify-content-center',
                          'align-items-center',
                          styles.ff,
                          hasFocus ? styles.ffFocused : styles.ffUnfocused,
                        )}
                      >
                        <img alt="FastForward" />
                        {playbackRate > 1 && (
                          <div className={classNames(styles.playbackRate)}>
                            <FormattedMessage
                              {...messages.playbackRateText}
                              values={{ speed: playbackRateText }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  />
                </div>
              )}
            </FocusGroup>
          )}
          {!isLimitedControls && (
            <div className="d-flex w-100">
              <PlaybackTimeline
                className={classNames(
                  'flex-grow-1 align-items-center',
                  styles.timeline,
                )}
                progressInSeconds={progress}
                durationInSeconds={duration}
              />
            </div>
          )}
          {
            <div
              className={classNames(
                'w-100 text-truncate text-center text-capitalize',
              )}
            >
              <div className={classNames(styles.discoveryRowTitle)}>
                Browse Live Channels
              </div>
              <div
                className={classNames(
                  'w-100 text-truncate text-center',
                  styles.chevron,
                )}
              />
            </div>
          }
        </div>
        {isCcEnabled && (
          <ClosedCaptionsOverlay
            className={classNames(styles.ccOverlay, {
              [styles.withLimitedControls]:
                isPlayerOverlayVisible && isLimitedControls,
              [styles.withControls]:
                isPlayerOverlayVisible && !isLimitedControls,
            })}
          />
        )}
        {isAdActive && (
          <div className={styles.adCountdown}>
            <FormattedMessage
              {...messages.adCountdown}
              values={{ countdown: adCountdown > 0 ? adCountdown : '' }}
            />
          </div>
        )}
        <PlayerNotification progress={position} duration={duration} />
        {isPreGame(video) && (
          <PreGameOverlay video={video} bannerMode={bannerMode} />
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

PlayerOverlay.propTypes = {
  onPrevVideo: PropTypes.func,
  onNextVideo: PropTypes.func,
  fullScreenPlayer: PropTypes.bool,
};

PlayerOverlay.defaultProps = {
  onPrevVideo: () => null,
  onNextVideo: () => null,
  fullScreenPlayer: false,
};

export default PlayerOverlay;
